import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ApiError, authService } from "@/services";
import { useState } from "react";

/**
 * Sign in with a code sent to your email.
 *
 * Here so that a company on Microsoft, Zoho or anything else is not blocked
 * while OIDC is still to come — the address is the one thing every identity
 * provider agrees on.
 *
 * A CODE, NOT A LINK. Corporate email security gateways follow links in
 * incoming mail to scan them, which consumes a single-use link before the
 * person clicks it. Partner admins sit behind exactly those gateways.
 */
export function EmailSignIn({ onSignedIn }: { onSignedIn: () => void }) {
	const [stage, setStage] = useState<"email" | "code">("email");
	const [email, setEmail] = useState("");
	const [code, setCode] = useState("");
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);

	async function send() {
		setBusy(true);
		setError(null);
		try {
			await authService.requestEmailCode(email.trim());
			// Always advances, even for an address with no account — the server
			// answers identically by design, and branching here would leak
			// exactly what it refuses to.
			setStage("code");
		} catch (e) {
			setError(
				e instanceof ApiError && e.status === 400
					? "That doesn't look like an email address."
					: "Couldn't send a code just now. Try again.",
			);
		} finally {
			setBusy(false);
		}
	}

	async function verify() {
		setBusy(true);
		setError(null);
		try {
			await authService.verifyEmailCode(email.trim(), code.trim());
			onSignedIn();
		} catch (e) {
			setError(
				e instanceof ApiError
					? e.message
					: "That code isn't valid. Request a new one and try again.",
			);
		} finally {
			setBusy(false);
		}
	}

	if (stage === "email") {
		return (
			<div className="flex w-full max-w-xs flex-col gap-2">
				<Input
					type="email"
					autoComplete="email"
					value={email}
					onChange={(e) => setEmail(e.target.value)}
					onKeyDown={(e) => e.key === "Enter" && email.trim() && send()}
					placeholder="you@company.com"
				/>
				<Button
					variant="outline"
					disabled={!email.trim() || busy}
					onClick={send}
				>
					{busy ? "Sending…" : "Email me a code"}
				</Button>
				{error ? <p className="text-caption text-error">{error}</p> : null}
			</div>
		);
	}

	return (
		<div className="flex w-full max-w-xs flex-col gap-2">
			{/*
			 * Says WHERE the code went. Someone who mistyped their address will
			 * otherwise sit waiting for an email that was never going to arrive.
			 */}
			<p className="text-caption text-muted-foreground">
				If <span className="text-heading">{email}</span> can sign in, a 6-digit
				code is on its way. It expires in 5 minutes.
			</p>
			<Input
				inputMode="numeric"
				autoComplete="one-time-code"
				maxLength={6}
				value={code}
				onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
				onKeyDown={(e) => e.key === "Enter" && code.length === 6 && verify()}
				placeholder="123456"
				className="tabular text-center tracking-[0.4em]"
			/>
			<Button disabled={code.length !== 6 || busy} onClick={verify}>
				{busy ? "Checking…" : "Sign in"}
			</Button>
			{error ? <p className="text-caption text-error">{error}</p> : null}
			<button
				type="button"
				className="text-caption text-muted-foreground underline-offset-2 hover:underline"
				onClick={() => {
					setStage("email");
					setCode("");
					setError(null);
				}}
			>
				Use a different address
			</button>
		</div>
	);
}
